import React, { useMemo } from "react";
import { Users, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/hooks";
import { Session } from "../features/sessions/models/Session";
import { buildAttendanceRoster } from "../features/sessions/utils/buildAttendanceRoster";
import { selectAllSessionVisits, visitsKey } from "../features/sessions/visits-slice";
import {
  VisitOutcome,
  outcomeSide,
} from "../features/sessions/models/VisitOutcome";
import { selectMembers, selectGroups } from "../features/members/members-slice";
import { isSessionScheduledForFuture } from "../utils/session-utils";

interface SessionParticipantsSummaryProps {
  session: Session;
  className?: string;
}

/**
 * Compact participant-count + attendance indicator for session cards. Upcoming
 * sessions show how many people are expected; past sessions also show how many
 * attended vs. were absent (from the mock visits store), or "Not marked" when
 * attendance hasn't been recorded.
 *
 * Reads group sizes from the members/groups directory. The host page is
 * responsible for loading that directory (see useEnsureDirectoryLoaded) — this
 * row component intentionally doesn't fetch, since it mounts many times at once.
 */
const SessionParticipantsSummary: React.FC<SessionParticipantsSummaryProps> = ({
  session,
  className = "",
}) => {
  const { t } = useTranslation();
  const members = useAppSelector(selectMembers);
  const groups = useAppSelector(selectGroups);
  const allVisits = useAppSelector(selectAllSessionVisits);

  const isPast = !isSessionScheduledForFuture(session);

  const { total, present, absent, marked } = useMemo(() => {
    const roster = buildAttendanceRoster(session, members, groups);
    const saved =
      allVisits[visitsKey(session.id, session.dateStart.slice(0, 10))]?.outcomes ?? {};
    let presentCount = 0;
    let absentCount = 0;
    for (const id of roster.memberIds) {
      const side = outcomeSide(saved[id] ?? VisitOutcome.Unmarked);
      if (side === "present") presentCount++;
      else if (side === "absent") absentCount++;
    }
    return {
      total: roster.totalCount,
      present: presentCount,
      absent: absentCount,
      marked: Object.keys(saved).length > 0,
    };
  }, [session, members, groups, allVisits]);

  if (total === 0) return null;

  return (
    <div className={`inline-flex items-center gap-2 text-sm ${className}`.trim()}>
      <span
        className="inline-flex items-center gap-1 text-grey-30 dark:text-grey-70"
        title={t("sessions.attendance.participantsTitle", { n: total })}
      >
        <Users size={15} />
        {total}
      </span>

      {isPast && marked && (
        <span
          className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"
          title={t("sessions.attendance.attendedTitle", { n: present })}
        >
          <Check size={15} />
          {present}
        </span>
      )}

      {isPast && marked && absent > 0 && (
        <span
          className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"
          title={t("sessions.attendance.absentTitle", { n: absent })}
        >
          <X size={15} />
          {absent}
        </span>
      )}

      {isPast && !marked && (
        <span className="text-grey-40 dark:text-grey-60">
          {t("sessions.attendance.notMarked")}
        </span>
      )}
    </div>
  );
};

export default SessionParticipantsSummary;
